/** Private recorder diagnostics only: never publish this data on match/SDK streams. */
export function redactDiagnosticText(value: string, incomplete = false): string {
    // Only malformed whole-frame previews may treat EOF as a closing quote.
    // A stored chunk ending mid-value must not be "repaired" independently.
    if (incomplete) value = value.replace(/(\b[\w-]{0,64}(?:token|password|secret|authorization|cookie|credential|ticket|guid|masterKey)[\w-]{0,32}["']?\s*[:=]\s*)(["'])(?:\\.|(?!\2)[^\\])*?(?:\2|$)/gi, '$1$2[redacted]$2');
    return value.replace(/(?:https?|wss?):\/\/[^\s"<>]+/gi, '[url]')
        .replace(/\bBearer\s+[A-Za-z0-9._~+\/=-]+/gi, 'Bearer [redacted]')
        .replace(/(\b[\w-]{0,64}(?:token|password|secret|authorization|cookie|credential|ticket|guid|masterKey)[\w-]{0,32}["']?\s*[:=]\s*)(["'])(?:\\.|(?!\2)[^\\])*?\2/gi, '$1$2[redacted]$2')
        .replace(/((?:\b[\w-]{0,64}(?:token|password|secret|authorization|cookie|credential|ticket|guid|masterKey)[\w-]{0,32})\s*["']?\s*[:=]\s*["']?)[^\s,"'}]+/gi, '$1[redacted]')
        .replace(/\br:[a-zA-Z0-9]{16,}\b/g, '[session]')
        .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '');
}

/** Redact before splitting a frame, including secrets spanning a chunk boundary. */
export function sanitizeBnetWirePayload(raw: string): { text: string; truncated: boolean; format: string; messageType?: string } {
    let nodes = 0, truncated = false;
    const visit = (value: any, depth = 0): any => {
        if (++nodes > 100000 || depth > 32) { truncated = true; return '[limit]'; }
        if (typeof value === 'string') return redactDiagnosticText(value);
        if (!value || typeof value !== 'object') return value;
        if (Array.isArray(value)) {
            const result = [];
            for (const child of value) {
                if (nodes >= 100000) { truncated = true; result.push('[limit]'); break; }
                result.push(visit(child, depth + 1));
            }
            return result;
        }
        const result = Object.create(null);
        for (const key of Object.keys(value)) {
            if (nodes >= 100000) { truncated = true; result['[limit]'] = true; break; }
            // Connection secrets and unrelated contact/session details are never evidence.
            result[redactDiagnosticText(key)] = /token|password|secret|authorization|cookie|credential|ticket|guid|session|email|address|phone|masterkey/i.test(key)
                ? '[redacted]' : visit(value[key], depth + 1);
        }
        return result;
    };
    try {
        const clean = visit(JSON.parse(raw));
        return { text: JSON.stringify(clean), truncated, format: 'json',
            messageType: typeof clean?.messageType === 'string' ? clean.messageType.slice(0, 80) : '' };
    }
    catch { return { text: redactDiagnosticText(raw, true).slice(0, 4096), truncated: raw.length > 4096, format: 'malformed-json' }; }
}

/** Paths are removed from runtime text, never from serialized/chunked wire JSON. */
export function redactRuntimeDiagnosticText(value: string): string {
    return redactDiagnosticText(value).replace(/\b[A-Za-z]:[\\/][^\r\n"<>|]*/g, '[path]')
        .replace(/\\\\[^\s"<>]+/g, '[path]')
        .replace(/\/(?:home|Users)\/[^\s"<>]+/g, '[path]');
}

/** Strict, shared allowlist for private runtime health and lifecycle records. */
export function sanitizeRuntimeDiagnostic(value: any): any | undefined {
    if (!value || !['runtime-health', 'runtime-event'].includes(value.kind)) return undefined;
    const clean: any = { kind: value.kind, version: 1 };
    const text = (v: any, max = 160) => typeof v === 'string' ? redactRuntimeDiagnosticText(v).slice(0, max) : '';
    const number = (v: any, max = Number.MAX_SAFE_INTEGER) => typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.min(v, max) : null;
    for (const key of ['event', 'phase', 'nativeStateName', 'platform', 'arch', 'osRelease', 'electron', 'node', 'chrome',
        'clientVersion', 'rendererRelease', 'bootstrapBuild', 'desktopSessionId', 'realm', 'hostStatus', 'category'])
        if (typeof value[key] === 'string') clean[key] = text(value[key]);
    for (const key of ['uptimeMs', 'phaseAgeMs', 'stateAgeMs', 'nativeState', 'hostNativeState', 'lastMessageAgeMs', 'lastDataAgeMs', 'lastGameDataAgeMs', 'lastGameTimeAgeMs', 'lastLogAgeMs',
        'gameTime', 'masterState', 'masterReconnects', 'masterDroppedMessages', 'logicalProcessors', 'totalMemoryMb', 'freeMemoryMb',
        'mainRssMb', 'mainHeapMb', 'cpuWindowMs', 'mainCpuPercent', 'eventLoopResolutionMs', 'eventLoopMaxMs', 'eventLoopP99Ms', 'nativeStartAttempts',
        'logConnections', 'dataConnections', 'nativeMessages', 'overlayWindows', 'interactiveOverlays', 'relayClients', 'relayUpdates',
        'pendingEntries', 'pendingBytes', 'droppedEntries', 'lastUploadAgeMs', 'count', 'suppressed'])
        if (Object.prototype.hasOwnProperty.call(value, key)) clean[key] = number(value[key]);
    for (const key of ['isReplay', 'isObserver', 'polling', 'highResolution', 'logPipeListening', 'dataPipeListening', 'visible', 'online'])
        if (typeof value[key] === 'boolean') clean[key] = value[key];
    if (Number.isFinite(value.clockOffsetMs)) clean.clockOffsetMs = Math.max(-8640000000000000, Math.min(8640000000000000, value.clockOffsetMs));
    if (Number.isSafeInteger(value.code)) clean.code = value.code;
    if (value.error) clean.error = text(value.error, 2048);
    if (value.stack) clean.stack = text(value.stack, 4096);
    if (Array.isArray(value.processes)) clean.processes = value.processes.slice(0, 24).map((p: any) => ({
        pid: number(p?.pid), type: text(p?.type, 32), role: text(p?.role, 32), cpuPercent: number(p?.cpuPercent, 100), workingSetMb: number(p?.workingSetMb)
    }));
    if (Array.isArray(value.files)) clean.files = value.files.slice(0, 4).map((f: any) => ({
        name: ['w3blib.dll', 'w3blibc.dll', 'libeay32.dll', 'ssleay32.dll'].includes(f?.name) ? f.name : 'unknown',
        status: ['ok', 'missing', 'unreadable', 'too-large'].includes(f?.status) ? f.status : 'unknown',
        loadedFrom: ['bundled', 'other', 'not-loaded'].includes(f?.loadedFrom) ? f.loadedFrom : 'unknown',
        loadedSha256: typeof f?.loadedSha256 === 'string' && /^[a-f0-9]{64}$/.test(f.loadedSha256) ? f.loadedSha256 : '',
        sha256: typeof f?.sha256 === 'string' && /^[a-f0-9]{64}$/.test(f.sha256) ? f.sha256 : '', bytes: number(f?.bytes)
    }));
    return clean;
}
